import { useCallback } from "react";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import { projectStorageBasePath } from "@domain/project/paths";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";

function resizeImage(file, maxWidth = 1920, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    };
    img.src = url;
  });
}

export default function useHeroImage() {
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [heroImage, setHeroImageDoc] = useFirestoreDoc("hero-image", null);

  // Use an already-uploaded image URL (e.g. from Design Book or space gallery)
  const setHeroFromUrl = useCallback(
    (url) => {
      if (!url) return;
      setHeroImageDoc({ url, updatedAt: new Date().toISOString() });
    },
    [setHeroImageDoc]
  );

  // Upload a new file to Storage then persist its URL
  const setHeroFromFile = useCallback(
    async (file) => {
      if (!user || !activeProject || !file) return;
      const blob = await resizeImage(file);
      const path = `${projectStorageBasePath(activeProject)}/hero-image/cover.jpg`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob);
      const url = await getDownloadURL(sref);
      setHeroImageDoc({ url, storagePath: path, updatedAt: new Date().toISOString() });
    },
    [user, activeProject, setHeroImageDoc]
  );

  const removeHero = useCallback(() => {
    setHeroImageDoc(null);
  }, [setHeroImageDoc]);

  return { heroImage, setHeroFromUrl, setHeroFromFile, removeHero };
}
